import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Calendar, IndianRupee, TrendingUp, Clock,
  CheckCircle2, XCircle, AlertTriangle, FileText, CreditCard, ChevronDown, ChevronUp
} from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
}

export function ParentPortalPage() {
  const { currentUser } = useApp();
  const [dashboard, setDashboard] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [fees, setFees] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [attendanceMonth, setAttendanceMonth] = useState('');
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);

  useEffect(() => {
    loadDashboard();
    loadRazorpayConfig();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadChildAttendance(selectedChild.studentId);
      loadChildFees(selectedChild.studentId);
    }
  }, [selectedChild, attendanceMonth]);

  const loadDashboard = async () => {
    setIsLoading(true);
    try {
      const data = await api.getParentDashboard();
      setDashboard(data);
      if (data.children?.length > 0) {
        setSelectedChild(data.children[0]);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRazorpayConfig = async () => {
    try {
      const config = await api.getRazorpayConfig();
      setRazorpayConfig(config);
    } catch (e) {
      console.error('Failed to load Razorpay config:', e);
    }
  };

  const loadChildAttendance = async (studentId) => {
    try {
      const data = await api.getParentChildAttendance(studentId, attendanceMonth || undefined);
      setAttendance(data);
    } catch (error) {
      console.error('Failed to load attendance:', error);
    }
  };

  const loadChildFees = async (studentId) => {
    try {
      const data = await api.getParentChildFees(studentId);
      setFees(data);
    } catch (error) {
      console.error('Failed to load fees:', error);
    }
  };

  const handlePayOnline = async (invoice) => {
    setPayingInvoice(invoice.invoiceId);
    try {
      const orderData = await api.createRazorpayOrder(invoice.invoiceId);

      if (orderData.mode === 'mock') {
        // Mock mode: simulate payment verification
        await api.verifyRazorpayPayment({
          razorpay_order_id: orderData.orderId,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
          invoiceId: invoice.invoiceId,
        });
        toast.success('Mock payment recorded successfully');
        loadChildFees(selectedChild.studentId);
        loadDashboard();
        return;
      }

      // Real Razorpay flow
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Actanon Technologies',
        image: `${window.location.origin}/actanon-brand.png`,
        description: `${orderData.feeName} - ${orderData.studentName}`,
        order_id: orderData.orderId,
        handler: async (response) => {
          try {
            await api.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              invoiceId: invoice.invoiceId,
            });
            toast.success('Payment successful!');
            loadChildFees(selectedChild.studentId);
            loadDashboard();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: currentUser?.name || '',
          email: currentUser?.email || '',
          contact: currentUser?.phone || currentUser?.contactNumber || '',
        },
        theme: { color: '#1f3354' },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Payment gateway not loaded. Please refresh and try again.');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to initiate payment');
    } finally {
      setPayingInvoice(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present': return <Badge className="bg-emerald-500/10 text-emerald-600">Present</Badge>;
      case 'absent': return <Badge className="bg-red-500/10 text-red-600">Absent</Badge>;
      case 'late': return <Badge className="bg-amber-500/10 text-amber-600">Late</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getFeeStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <Badge className="bg-emerald-500/10 text-emerald-600">Paid</Badge>;
      case 'partial': return <Badge className="bg-amber-500/10 text-amber-600">Partial</Badge>;
      case 'pending': return <Badge className="bg-red-500/10 text-red-600">Pending</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Month options for filter
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: val, label });
  }

  if (isLoading) {
    return (
      <div data-testid="parent-portal-page" className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const child = selectedChild;
  const childDashboard = dashboard?.children?.find(c => c.studentId === child?.studentId);

  return (
    <div data-testid="parent-portal-page" className="space-y-6 animate-fade-in">
      <PageHeader
        title="Parent Portal"
        description={`Welcome, ${currentUser?.name || 'Parent'}`}
      />

      {/* Child Selector (if multiple children) */}
      {dashboard?.children?.length > 1 && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Viewing:</span>
          <Select
            value={child?.studentId || ''}
            onValueChange={(val) => {
              const c = dashboard.children.find(ch => ch.studentId === val);
              if (c) setSelectedChild(c);
            }}
          >
            <SelectTrigger className="w-[280px]" data-testid="child-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dashboard.children.map(ch => (
                <SelectItem key={ch.studentId} value={ch.studentId}>
                  {ch.fullName} (Class {ch.class}-{ch.section})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Child Overview Cards */}
      {childDashboard && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="Student"
            value={childDashboard.fullName}
            description={`Class ${childDashboard.class}-${childDashboard.section} | Roll ${childDashboard.rollNumber || '-'}`}
            icon={Users}
            delay={0}
          />
          <StatsCard
            title="Attendance"
            value={`${childDashboard.attendance.percentage}%`}
            description={`${childDashboard.attendance.present}/${childDashboard.attendance.total} days (30d)`}
            icon={Calendar}
            delay={0.05}
          />
          <StatsCard
            title="Fees Paid"
            value={formatCurrency(childDashboard.fees.totalPaid)}
            description={`of ${formatCurrency(childDashboard.fees.totalDue)} total`}
            icon={IndianRupee}
            delay={0.1}
          />
          <StatsCard
            title="Pending Fees"
            value={formatCurrency(childDashboard.fees.pending)}
            description={`${childDashboard.fees.pendingInvoices} unpaid invoice(s)`}
            icon={AlertTriangle}
            delay={0.15}
          />
        </div>
      )}

      {/* Tabs: Attendance & Fees */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList data-testid="parent-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">Fees & Invoices</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">Assignments</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Attendance Summary */}
            <DataCard title="Attendance Summary (30 days)">
              {childDashboard ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">{childDashboard.attendance.percentage}%</p>
                        <p className="text-sm text-muted-foreground">Overall Attendance</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 rounded-lg bg-emerald-500/10">
                      <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
                      <p className="text-lg font-bold">{childDashboard.attendance.present}</p>
                      <p className="text-xs text-muted-foreground">Present</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-red-500/10">
                      <XCircle className="w-5 h-5 mx-auto text-red-500 mb-1" />
                      <p className="text-lg font-bold">{childDashboard.attendance.absent}</p>
                      <p className="text-xs text-muted-foreground">Absent</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-blue-500/10">
                      <Calendar className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                      <p className="text-lg font-bold">{childDashboard.attendance.total}</p>
                      <p className="text-xs text-muted-foreground">Total Days</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground py-8 text-center">No data available</p>
              )}
            </DataCard>

            {/* Fee Summary */}
            <DataCard title="Fee Summary">
              {childDashboard ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <IndianRupee className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">{formatCurrency(childDashboard.fees.pending)}</p>
                        <p className="text-sm text-muted-foreground">Pending Amount</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                      <p className="text-sm text-muted-foreground">Total Due</p>
                      <p className="text-lg font-bold">{formatCurrency(childDashboard.fees.totalDue)}</p>
                    </div>
                    <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                      <p className="text-sm text-muted-foreground">Total Paid</p>
                      <p className="text-lg font-bold text-emerald-600">{formatCurrency(childDashboard.fees.totalPaid)}</p>
                    </div>
                  </div>
                  {childDashboard.fees.pendingInvoices > 0 && (
                    <Button
                      className="w-full bg-brand-deep-blue hover:bg-brand-deep-blue/90"
                      onClick={() => setActiveTab('fees')}
                      data-testid="view-pending-invoices-btn"
                    >
                      View {childDashboard.fees.pendingInvoices} Pending Invoice(s)
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground py-8 text-center">No data available</p>
              )}
            </DataCard>
          </div>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <DataCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Attendance Records</h3>
              <Select value={attendanceMonth} onValueChange={setAttendanceMonth}>
                <SelectTrigger className="w-[200px]" data-testid="attendance-month-filter">
                  <SelectValue placeholder="All months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {monthOptions.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Attendance Stats Bar */}
            {attendance?.stats && (
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-lg font-bold">{attendance.stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="text-center p-2 rounded bg-emerald-500/10">
                  <p className="text-lg font-bold text-emerald-600">{attendance.stats.present}</p>
                  <p className="text-xs text-muted-foreground">Present</p>
                </div>
                <div className="text-center p-2 rounded bg-red-500/10">
                  <p className="text-lg font-bold text-red-600">{attendance.stats.absent}</p>
                  <p className="text-xs text-muted-foreground">Absent</p>
                </div>
                <div className="text-center p-2 rounded bg-amber-500/10">
                  <p className="text-lg font-bold text-amber-600">{attendance.stats.late}</p>
                  <p className="text-xs text-muted-foreground">Late</p>
                </div>
              </div>
            )}

            {attendance?.records?.length > 0 ? (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {attendance.records.map((rec, i) => (
                      <tr key={rec.attendanceId || i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <td className="px-4 py-3 text-sm font-medium">{rec.date}</td>
                        <td className="px-4 py-3">{getStatusBadge(rec.status)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{rec.remarks || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No attendance records found</p>
              </div>
            )}
          </DataCard>
        </TabsContent>

        {/* Fees Tab */}
        <TabsContent value="fees">
          <DataCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Invoices & Payments</h3>
              {fees?.summary && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">
                    Total: <strong>{formatCurrency(fees.summary.totalDue)}</strong>
                  </span>
                  <span className="text-emerald-600">
                    Paid: <strong>{formatCurrency(fees.summary.totalPaid)}</strong>
                  </span>
                  <span className="text-red-600">
                    Pending: <strong>{formatCurrency(fees.summary.totalPending)}</strong>
                  </span>
                </div>
              )}
            </div>

            {fees?.invoices?.length > 0 ? (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Fee</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Paid</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Pending</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Due Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {fees.invoices.map((inv) => {
                      const pending = inv.amount - (inv.paidAmount || 0);
                      return (
                        <tr key={inv.invoiceId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-sm font-medium">{inv.feeName}</td>
                          <td className="px-4 py-3 text-sm">{formatCurrency(inv.amount)}</td>
                          <td className="px-4 py-3 text-sm text-emerald-600">{formatCurrency(inv.paidAmount || 0)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-red-600">{formatCurrency(pending)}</td>
                          <td className="px-4 py-3 text-sm">{inv.dueDate}</td>
                          <td className="px-4 py-3">{getFeeStatusBadge(inv.status)}</td>
                          <td className="px-4 py-3 text-center">
                            {inv.status !== 'paid' && (
                              <Button
                                size="sm"
                                className="bg-brand-deep-blue hover:bg-brand-deep-blue/90"
                                onClick={() => handlePayOnline(inv)}
                                disabled={payingInvoice === inv.invoiceId}
                                data-testid={`pay-online-${inv.invoiceId}`}
                              >
                                {payingInvoice === inv.invoiceId ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                                ) : (
                                  <CreditCard className="w-4 h-4 mr-1" />
                                )}
                                Pay Online
                              </Button>
                            )}
                            {inv.status === 'paid' && (
                              <span className="text-sm text-emerald-600 flex items-center justify-center gap-1">
                                <CheckCircle2 className="w-4 h-4" /> Paid
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No invoices found</p>
              </div>
            )}

            {/* Payment History */}
            {fees?.payments?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Payment History</h4>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Method</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {fees.payments.map((p) => (
                        <tr key={p.paymentId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-sm">{new Date(p.createdAt).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm font-medium text-emerald-600">{formatCurrency(p.amount)}</td>
                          <td className="px-4 py-3 text-sm capitalize">{(p.paymentMethod || '').replace('_', ' ')}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{p.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DataCard>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments">
          <DataCard title="Child Assignments" subtitle={selectedChild ? `Assignments for ${selectedChild.name}` : 'Select a child'}>
            {(() => {
              const childAssignments = [
                { _id: 'pa_1', title: 'Chapter 5: Quadratic Equations', type: 'Assignment', subject: 'Mathematics', dueDate: '2026-04-15', status: 'Pending' },
                { _id: 'pa_2', title: 'Unit Test - Periodic Table', type: 'Test', subject: 'Chemistry', dueDate: '2026-04-12', status: 'Pending' },
                { _id: 'pa_3', title: 'Essay: Environmental Conservation', type: 'Homework', subject: 'English', dueDate: '2026-04-10', status: 'Submitted' },
                { _id: 'pa_4', title: 'Book Summary: The Guide', type: 'Summary', subject: 'English', dueDate: '2026-04-08', status: 'Evaluated', score: '8/10' },
              ];
              return (
                <div className="space-y-2 mt-3">
                  {childAssignments.map(a => (
                    <div key={a._id} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`parent-assignment-${a._id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{a.title}</p>
                          <Badge variant="outline" className="text-[10px]">{a.type}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{a.subject} · Due: {a.dueDate}{a.score ? ` · Score: ${a.score}` : ''}</p>
                      </div>
                      <Badge className={
                        a.status === 'Evaluated' ? 'bg-emerald-500/10 text-emerald-600' :
                        a.status === 'Submitted' ? 'bg-blue-500/10 text-blue-600' :
                        'bg-amber-500/10 text-amber-600'
                      }>{a.status}</Badge>
                    </div>
                  ))}
                </div>
              );
            })()}
          </DataCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
